<template>
  <modal
    :header="true"
    :footer="false"
    :close-button="true"
    :auto-height="true"
    disable-click-outside
    class="poll-display-modal"
    data-test="graphics-pollDisplayModal"
    @close="onCancel"
  >
    <template #modal-header>
      <h2>Connect your poll</h2>
    </template>

    <template #modal-body>
      <div class="modal-body-wrapper">
        <div
          v-if="loading"
          class="text-center padding-lg"
        >
          <span class="fas fa-spinner fa-spin" />
          Loading polls...
        </div>

        <div
          v-else-if="error"
          class="text-center padding-lg text-warning"
        >
          <p>{{ error }}</p>
        </div>

        <div
          v-else
          class="modal-content-wrapper"
        >
          <div class="dropdowns-container">
            <select
              v-model="selectedPollId"
              class="form-control"
              data-test="graphics-pollDisplay-pollSelect"
              @change="onPollChange"
            >
              <option
                :value="null"
                disabled
              >
                Select a poll
              </option>
              <option
                v-for="poll in polls"
                :key="poll.pollId"
                :value="poll.pollId"
              >
                {{ poll.title }}
              </option>
            </select>

            <select
              v-model="selectedDisplayId"
              class="form-control"
              :disabled="!selectedPollId"
              data-test="graphics-pollDisplay-displaySelect"
            >
              <option
                :value="null"
                disabled
              >
                Select a layout
              </option>
              <option
                v-for="display in availableDisplays"
                :key="display.displayId"
                :value="display.displayId"
              >
                {{ display.title }}
              </option>
            </select>
          </div>

          <div class="modal-footer-actions">
            <button
              class="btn btn-style-b btn-cancel"
              data-test="graphics-pollDisplay-cancel"
              @click="onCancel"
            >
              Cancel
            </button>
            <button
              class="btn btn-style-a btn-add-display"
              :disabled="!selectedDisplayId"
              data-test="graphics-pollDisplay-confirm"
              @click="confirmSelection"
            >
              Connect
            </button>
          </div>
        </div>
      </div>
    </template>
  </modal>
</template>

<script>
import Modal from './Modal.vue';
import Polls from '../../services/polls';

export default {
  emits: ['close', 'select'],
  name: 'PollDisplayModal',
  components: {
    Modal,
  },
  data() {
    return {
      polls: [],
      pollsDetails: {},
      loading: true,
      error: null,
      selectedPollId: null,
      selectedDisplayId: null,
    };
  },
  computed: {
    availableDisplays() {
      if (!this.selectedPollId || !this.pollsDetails[this.selectedPollId]) {
        return [];
      }

      const displays = this.pollsDetails[this.selectedPollId].displays || [];
      return displays.filter((d) => d.displayType !== 'poll');
    },
  },
  async mounted() {
    await this.loadPolls();
  },
  methods: {
    async loadPolls() {
      try {
        this.error = null;
        this.loading = true;

        const response = await Polls.getPolls({
          limit: 100,
        });

        if (!response || !response.polls) {
          this.polls = [];
          this.loading = false;
          return;
        }

        this.polls = response.polls;
        this.loading = false;
      } catch (err) {
        this.error = 'Failed to load polls. Please try again.';
        this.loading = false;
        console.error('Error loading polls:', err);
      }
    },

    async onPollChange() {
      this.selectedDisplayId = null;

      if (!this.selectedPollId) {
        return;
      }

      if (this.pollsDetails[this.selectedPollId]) {
        return;
      }

      try {
        const pollDetails = await Polls.getPollById(this.selectedPollId);
        this.pollsDetails[this.selectedPollId] = pollDetails;
      } catch (err) {
        console.error('Error loading poll details:', err);
        this.error = 'Failed to load poll layouts. Please try again.';
      }
    },

    onCancel() {
      this.$emit('close');
    },

    confirmSelection() {
      if (!this.selectedDisplayId || !this.selectedPollId) {
        return;
      }

      const display = this.availableDisplays.find(
        (d) => d.displayId === this.selectedDisplayId,
      );

      if (display) {
        this.$emit('select', {
          ...display,
          pollId: this.selectedPollId,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.modal-container) {
  max-width: 420px !important;
  width: 90vw !important;
}

:deep(.modal-body) {
  padding: 1.5rem;
}

.modal-body-wrapper {
  display: flex;
  flex-direction: column;
}

.modal-content-wrapper {
  display: flex;
  flex-direction: column;
}

.dropdowns-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
}

.form-control {
  width: 100%;
  height: 38px;
  padding: 0 1rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 16px;
  line-height: 36px;
  background-color: white;
  color: #374151;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00b4cc;
    box-shadow: 0 0 0 3px rgba(0, 180, 204, 0.1);
  }

  &:disabled {
    background-color: #f9fafb;
    color: #d1d5db;
    cursor: not-allowed;
  }
}

select.form-control {
  padding-right: 2.5rem;
  background-image: url(/img/triangle-down.svg);
  background-size: 8px auto;
  background-position: calc(100% - 1rem) 50%;
  background-repeat: no-repeat;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.text-center {
  text-align: center;
}

.padding-lg {
  padding: 2rem;
  font-size: 1rem;
}

.text-warning {
  color: #f59e0b;
}

.modal-footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.btn-add-display,
.btn-cancel {
  padding: 0.625rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
</style>
